import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightning, Sun, Drop, Wrench, Gear } from "@phosphor-icons/react";
import { RightRail } from "@/components/customer/RightRail";
import { ChatComposer } from "@/components/customer/ChatComposer";
import { DisclaimerBar, InfoButton } from "@/components/customer/Disclaimer";
import { useCustomer } from "@/context/CustomerContext";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useVoiceEnabled } from "@/hooks/useVoiceConversation";
import { unlockAudio } from "@/lib/voice";

const CATEGORIES = [
  { icon: Lightning, title: "Electrical Issues", desc: "Lights, batteries, inverters, wiring", prompt: "I'm having an electrical issue with my caravan." },
  { icon: Sun, title: "Solar Questions", desc: "Charging, panels, MPPT, settings", prompt: "I have a question about my solar system." },
  { icon: Drop, title: "Water System", desc: "Pumps, tanks, plumbing, leaks", prompt: "I'm having a problem with my water system." },
  { icon: Wrench, title: "General Help", desc: "Maintenance, how-tos, specs", prompt: "I have a general maintenance question." },
];

const SUGGESTIONS = [
  "Why isn't my solar charging?",
  "How do I reset my inverter?",
  "My water pump keeps running",
  "What does this error code mean?",
];

export default function Home() {
  const navigate = useNavigate();
  const { caravan, newChat } = useCustomer();
  const isDesktop = useIsDesktop();
  const voiceEnabled = useVoiceEnabled();

  const start = (message, image = null) => {
    newChat();
    navigate("/chat", { state: { initialMessage: message, initialImage: image, fresh: true } });
  };

  const startVoice = () => {
    unlockAudio();
    newChat();
    navigate("/chat", { state: { voice: true } });
  };

  return (
    <div className="h-full flex">
      <div className="flex-1 min-w-0 flex flex-col overflow-y-auto grain relative">
        <div className="relative z-10 flex-1 flex flex-col px-5 sm:px-6 lg:px-10 py-6 sm:py-8 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          {/* top icons */}
          <div className="flex justify-end gap-2 mb-2">
            <InfoButton />
            <button
              onClick={() => navigate("/my-caravan")}
              className="w-9 h-9 rounded-full border border-neutral-700 flex items-center justify-center text-neutral-400 hover:text-white hover:border-neutral-500 transition-colors"
              data-testid="settings-button"
            >
              <Gear size={18} />
            </button>
          </div>

          {/* Hero */}
          <div className="max-w-3xl mx-auto w-full text-center pt-2 sm:pt-4">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="h-px w-8 sm:w-10 bg-gradient-to-r from-transparent to-[#73bf44]/60" />
              <span className="text-[0.7rem] sm:text-xs font-bold tracking-[0.3em] sm:tracking-[0.35em] text-[#73bf44]">OTM TECH AI</span>
              <span className="h-px w-8 sm:w-10 bg-gradient-to-l from-transparent to-[#73bf44]/60" />
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-heading font-extrabold tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-8xl bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent"
            >
              NICK
            </motion.h1>
            <div className="font-heading tracking-[0.22em] sm:tracking-[0.28em] text-xs sm:text-sm md:text-base text-white/90 mt-1 mb-4 sm:mb-6">
              YOUR CARAVAN'S TECHNICAL EXPERT
            </div>
            <p className="text-neutral-400 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              G'day{caravan?.customer_name ? `, ${caravan.customer_name.split(" ")[0]}` : ""}! I'm Nick, your AI technical expert for{" "}
              <span className="text-[#73bf44] font-medium">On The Move Caravans</span>. Ask me anything about your
              caravan's systems, troubleshoot issues, or get maintenance advice.
            </p>
          </div>

          {/* Categories */}
          <div className="max-w-4xl mx-auto w-full grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6 sm:mt-10">
            {CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.button
                  key={c.title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 + i * 0.06 }}
                  onClick={() => start(c.prompt)}
                  data-testid={`category-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#121212] border border-neutral-800 hover:border-[#73bf44] rounded-xl p-4 sm:p-5 text-left transition-all duration-300 hover:-translate-y-1 group"
                >
                  <Icon size={24} weight="duotone" className="text-[#73bf44] mb-2 sm:mb-3" />
                  <div className="font-heading font-semibold text-white text-sm sm:text-[0.95rem] leading-tight mb-1">{c.title}</div>
                  <div className="text-xs text-neutral-500 leading-snug">{c.desc}</div>
                </motion.button>
              );
            })}
          </div>

          {/* Composer */}
          <div className="mt-6 sm:mt-10">
            <ChatComposer onSend={({ message, image }) => start(message, image)} onVoice={voiceEnabled ? startVoice : undefined} autoFocus={isDesktop} />
            <div className="max-w-3xl mx-auto flex flex-wrap gap-2 mt-4 px-1">
              <span className="text-xs text-neutral-600 py-1.5">Try asking something like:</span>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => start(s)}
                  data-testid={`suggestion-${s.slice(0, 10).toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs text-neutral-300 bg-[#141414] border border-neutral-800 rounded-full px-3 py-1.5 hover:border-[#73bf44] hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-8 sm:pt-10">
            <div className="border-t border-neutral-900 pt-6">
              <DisclaimerBar />
            </div>
          </div>
        </div>
      </div>
      <RightRail />
    </div>
  );
}
